import { useEffect } from "react";
import Head from "next/head";
import Image from "next/image";
import { SectionLayout } from "@/components/SectionLayout";
import { Text, Heading } from "@/components/Text";
import { Button } from "@/components/Button";
import { YouTubeFacade } from "@/components/YouTubeFacade";
import hero from "/public/assets/images/homepage-logo.png";
import { LayoutX } from "@/components/LayoutX";
import { Reveal } from "@/utils/Reveal";
import { getHomepage } from "../../sanity/lib/getCmsData";
import { urlForImage } from "../../sanity/lib/image";

export async function getStaticProps() {
  const data = await getHomepage();
  return {
    props: {
      data,
    },
    revalidate: 10,
  };
}

export default function Home({ data }) {
  const heroBlockData = data.itemName;
  const introBlockData = data.introBlock;
  const focusBlockData = data.focusBlock;
  const testimonialsBlockData = data.testimonialsBlock;

  useEffect(() => {
    // Dynamically import AOS to reduce initial bundle size
    import("aos").then((AOS) => {
      import("aos/dist/aos.css");
      AOS.default.init({
        duration: 500,
        easing: "ease-in",
        once: true,
        delay: 25,
      });
    });
  }, []);
  return (
    <>
      <Head>
        <title>Red Ball Drills | Home</title>
        <meta
          name="description"
          content="Red Ball Drills (RBD) was created by Experior Group Inc. and it is the only US Department of Homeland Security SAFETY Act certified crisis management training program."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
        {/* Preload LCP hero background image */}
        <link
          rel="preload"
          as="image"
          href="/assets/images/homepage-hero.png"
          fetchpriority="high"
        />
      </Head>
      <Hero data={heroBlockData} />
      <Intro data={introBlockData} />
      <Focus data={focusBlockData} />
      <Main data={data} />
      <Testimonials data={testimonialsBlockData} />
    </>
  );
}

function Hero({ data }) {
  const { title } = data;
  return (
    <section className="relative h-[44rem] -mt-[8.3125rem] pt-[8.3125rem] flex items-center bg-no-repeat bg-cover bg-[position:40%] bg-[url('/assets/images/homepage-hero.png')] overflow-hidden">
      <div className="absolute inset-0 bg-white/80"></div>
      <div className="relative h-full w-full md:pl-8 flex items-center lg:justify-end">
        <div className="hidden lg:flex lg:justify-end lg:w-1/2 lg:pl-8">
          <div className="max-w-[585px] w-full">
            <Image
              src={hero}
              alt="Red Ball Drills simplified logo"
              className="w-96"
              priority
            />
          </div>
        </div>
        <div className="w-full md:max-w-full md:w-2/3 lg:w-1/2">
          <div
            data-aos="fade-left"
            data-aos-duration="800"
            className="px-5 md:px-0 py-9 md:py-14 w-full"
          >
            <div className="lg:max-w-[520px]">
              <Heading as="h1" size="hero" className="mb-6 md:mb-8">
                {title}
              </Heading>
              <Button role="contact" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Intro({ data }) {
  const { title, description, points, mainImage } = data;
  return (
    <SectionLayout>
      <Heading data-aos="fade-in" className="mb-6">
        {title}
      </Heading>
      <article className="flex flex-col md:flex-row gap-10">
        <div className="mx-auto md:mx-0 flex-1">
          <Image
            src={urlForImage(mainImage).width(476).height(398).url()}
            alt="U.S. Department of Homeland Security Safety Act Certification badge"
            width={476}
            height={398}
            priority
            sizes="(max-width: 768px) 100vw, 476px"
          />
        </div>
        <div className="flex-[1.15_1_0%] lg:flex-1">
          <Text className="mb-7">{description}</Text>
          <ul className="mb-8 md:mb-10 flex flex-col gap-5">
            {!!points?.length &&
              points.map((point) => {
                return (
                  <li key={point._key} className="flex gap-3">
                    <div>
                      <div className="mt-1 w-[1.125rem] h-[1.125rem] bg-rbd-primary rounded-full"></div>
                    </div>
                    <Text>{point.children[0].text}</Text>
                  </li>
                );
              })}
          </ul>
          <Button role="consultation" />
        </div>
      </article>
    </SectionLayout>
  );
}

function Focus({ data }) {
  const { title, focuses } = data;

  return (
    <SectionLayout>
      <Heading data-aos="fade-in" className="mb-6">
        {title}
      </Heading>
      <div className="mx-auto max-w-[854px] flex flex-col gap-10 md:flex-row md:justify-between md:flex-wrap">
        {!!focuses?.length &&
          focuses.map((focus) => {
            return (
              <article
                key={focus._key}
                className="flex flex-col items-center gap-5"
              >
                <Reveal>
                  <Image
                    width={168}
                    height={168}
                    src={urlForImage(focus.mainImage).url()}
                    alt=""
                  />
                </Reveal>
                <Heading as="h3" size="icon-card">
                  {focus.title}
                </Heading>
              </article>
            );
          })}
      </div>
    </SectionLayout>
  );
}

function Main({ data }) {
  const aboutBlockData = data.videoArticle;
  const lawEnforcementData = data.videoItem;
  const ourPrinciplesData = data.principleBlock;

  return (
    <section>
      <div className="relative lg:px-16">
        <section className="w-full my-20 md:my-[6.25rem] xl:my-[7.5rem] px-5 md:px-8">
          <div className="mx-auto max-w-[1170px]">
            <div className="relative">
              <div className="relative z-10 pt-14 lg:px-0 flex flex-col gap-16">
                <About data={aboutBlockData} />
              </div>
              <div className="absolute top-0 w-[calc(100%+1rem)] md:w-full lg:w-[calc(100%+8rem)] -left-2 md:left-0 lg:-left-16 h-full"></div>
            </div>
          </div>
        </section>
      </div>
      <LawEnforcement data={lawEnforcementData} />
      <div className="relative lg:px-16">
        <section className="w-full my-20 md:my-[6.25rem] xl:my-[7.5rem] px-5 md:px-8">
          <div className="mx-auto max-w-[1170px]">
            <div className="relative">
              <div className="relative z-10 pb-14 md:px-0 flex flex-col gap-16">
                <Principles data={ourPrinciplesData} />
              </div>
              <div className="absolute top-0 w-[calc(100%+1rem)] md:w-full lg:w-[calc(100%+8rem)] -left-2 md:left-0 lg:-left-16 h-full"></div>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
}

function About({ data }) {
  const { link, description, title } = data;
  return (
    <section className="flex flex-col gap-12 lg:flex-row">
      <div className="flex-1">
        <div className="lg:max-w-md">
          <Heading data-aos="fade-in" className="mb-6">
            {title}
          </Heading>
          <Text className="mb-10">{description}</Text>
          <Button />
        </div>
      </div>
      <div className="flex-1 flex items-center">
        <div className="relative rounded-xl overflow-hidden aspect-video w-full">
          <YouTubeFacade src={link} title={title} />
        </div>
      </div>
    </section>
  );
}

function LawEnforcement({ data }) {
  const { title, link } = data;
  return (
    <section className="py-20 md:py-[6.25rem] xl:py-[7.5rem] lg:px-16 w-full bg-rbd-secondary-base">
      <LayoutX>
        <Heading data-aos="fade-in" className="mb-6">
          {title}
        </Heading>
        <div>
          <div className="relative mx-auto max-w-4xl rounded-xl overflow-hidden aspect-video w-full">
            <YouTubeFacade src={link} title={title} />
          </div>
        </div>
      </LayoutX>
    </section>
  );
}

function Principles({ data }) {
  const { title, principles } = data;
  return (
    <section>
      <Heading data-aos="fade-in" className="mb-6">
        {title}
      </Heading>
      <div className="lg:px-8 flex flex-col min-[926px]:flex-row items-center min-[926px]:items-stretch min-[926px]:justify-between gap-8 ">
        {!!principles?.length &&
          principles.map((principle) => {
            return (
              <article
                key={principle._key}
                className="w-56 min-[926px]:w-80 min-h-[13.125rem] p-4 xl:p-8 flex flex-col gap-2 xl:gap-3 items-center text-center rounded-xl shadow-surround bg-rbd-base"
              >
                <Reveal>
                  <Image
                    src={urlForImage(principle.mainImage).url()}
                    alt=""
                    width={75}
                    height={75}
                  />
                </Reveal>
                <Heading as="h3" size="icon-card">
                  {principle.title}
                </Heading>
                <Text size="principles-card">{principle.description}</Text>
              </article>
            );
          })}
      </div>
    </section>
  );
}

function Testimonials({ data }) {
  const { title, testimonials } = data;
  return (
    <SectionLayout>
      <Heading data-aos="fade-in" className="mb-6">
        {title}
      </Heading>
      <section className="mb-10 md:mb-6 flex flex-col flex-wrap items-center xl:flex-row xl:justify-center xl:items-stretch gap-8 xl:gap-7 ">
        {!!testimonials?.length &&
          testimonials.map((testimonial, i) => (
            <figure
              key={i}
              className="px-5 py-9 md:px-9 max-w-[570px] md:min-h-[406px] shadow-surround rounded-xl flex flex-col bg-rbd-base"
            >
              <figcaption className="min-h-[4.5rem] mb-2">
                {testimonial.details.map((detail, j) => (
                  <Heading
                    key={j}
                    as="p"
                    size="title"
                    className="text-rbd-tertiary"
                  >
                    {detail}
                  </Heading>
                ))}
              </figcaption>
              <blockquote>
                <Text>{testimonial.review}</Text>
              </blockquote>
            </figure>
          ))}
      </section>
    </SectionLayout>
  );
}

function Benefits() {
  return (
    <section className="py-20 md:py-0 md:h-[40rem] flex items-center bg-no-repeat bg-cover bg-[position:40%] bg-[url('/assets/images/footer-banner.png')] overflow-hidden">
      <div className="h-full w-full flex flex-col md:flex-row items-end md:items-center ">
        <div className="w-full md:relative mb-6 md:mb-0 flex lg:justify-end md:w-1/2 md:h-[210px] lg:h-auto">
          <div className="px-5 md:pl-8 lg:pr-16 xl:pl-0 md:pr-0 md:absolute lg:static md:top-0 md:w-[470px] max-w-[585px] lg:w-full">
            <Heading data-aos="fade-in" className="mb-5">
              Benefits of Implementing RBD
            </Heading>
            <Text className="mb-8 md:mb-10">
              Whether your organization has a robust risk management program or
              is only the beginning of one, our drills engage your organization
              to gain critical perspectives and identify meaningful changes,
              resulting in a safer workplace for all.
            </Text>
            <Button />
          </div>
        </div>
        <div className="pl-9 md:pl-0 max-w-[513px] w-full md:max-w-full md:w-full lg:w-1/2 flex flex-col gap-9 items-end">
          <article
            data-aos="fade-right"
            data-aos-duration="800"
            className="h-[162px] max-w-[488px] lg:max-w-[96%] w-full pl-10 flex items-center rounded-l-4xl bg-rbd-base/40"
          >
            <div className="max-w-[240px]">
              <Text size="benefits-card">
                Develop benchmark policies and procedures which align to client
                culture
              </Text>
            </div>
          </article>
          <article
            data-aos="fade-left"
            data-aos-duration="800"
            className="h-[162px] max-w-[290px] lg:max-w-[75%] w-full pl-10 flex items-center rounded-l-4xl bg-rbd-base/40"
          >
            <div className="max-w-[220px]">
              <Text size="benefits-card">
                DEVELOPING A CULTURE OF SAFETY AWARENESS
              </Text>
            </div>
          </article>
          <article
            data-aos="fade-right"
            data-aos-duration="800"
            className="h-[162px] max-w-[447px] lg:max-w-[92%] w-full pl-10 flex items-center rounded-l-4xl bg-rbd-base/40"
          >
            <div className="max-w-[300px]">
              <Text size="benefits-card">
                MITIGATE RISK THROUGH PROCESS DEVELOPED FROM THE GROUND UP
              </Text>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
